import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { buildApprovalsLink, canApproveBuilds } from "../../lib/buildApproval";
import { useAuth } from "../../context/AuthContext";
import type { Deal, PMWorkbook, PMEagleview, PMPostBuild, PMSiteChecklist, TeamMember, PunchListItem } from "../../lib/types";
import DealLink from "../DealLink";
import PreFindingsCard from "../deal/PreFindingsCard";
import AddOnNotesCard from "../deal/AddOnNotesCard";
import DoNotPerformCard, { readDoNotPerform } from "../deal/DoNotPerformCard";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import {
  sendMaterialOrder, fetchLatestOrderSend, fmtSentStamp,
  type MaterialOrderSend, type SendOrderResult,
} from "../../lib/srsMaterialOrder";
import RescheduleBuildModal from "./actions/RescheduleBuildModal";
import PMScheduleBuildModal from "./actions/PMScheduleBuildModal";
import {
  buildCalendar, fetchCalendarStatus, fetchBuildCalendarLink, fetchOpenCallOff, fetchOpenCallOffs, fmtShortDate, srsDropDateISO,
  fmtBuildSpan, fmtBuildSpanCompact, buildDayLabel, normalizeBuildEnd, buildEndProblem, addWeekdaysISO, buildDayCount, todayISO,
  NotConfiguredError, REASON_LABEL, fetchLatestSchedule, canScheduleBuilds, SCHEDULE_FROM_STAGES, BEFORE_APPROVAL_STAGES, PAYMENT_PLAN_LABELS,
  type BuildCalendarLink, type BuildReschedule, type CalendarStatus, type UpsertEventResult, type BuildSchedule,
} from "../../lib/buildCalendar";
import { mergeSpecialInstructions, fmtUSD } from "../../../supabase/functions/build-calendar/eventText";

/* ═══════════════════════════════════════════════════════════════
   UNIT PRICES — SRS current pricing
   ═══════════════════════════════════════════════════════════════ */
const PRICES: Record<string, number> = {
  duration: 36.66,
  proedge: 69.0,
  starter: 49.0,
  rhino: 89.07,
  ice_water: 74.87,
  vent_sure: 10.36,
  pipe_jacks: 5.95,
  osb: 23.7,
  coil_nails: 45.0,
  cap_nails: 24.2,
  np1: 8.1,
  gutter_apron: 8.4,
  drip_edge: 9.45,
  step_flashing: 68.5,
  paint: 8.5,
};

/* Material line items — matches the exact spreadsheet row order */
const MATERIAL_ROWS: Array<{
  key: string;
  label: string;
  colorField?: boolean;
  formula: string;
}> = [
  { key: "duration", label: "Bundles OC Duration", colorField: true, formula: "(Sq w/waste × 3) + 4" },
  { key: "proedge", label: "Bundles OC ProEdge", formula: "(Hip + Ridge) / 25 round" },
  { key: "starter", label: "Bundles OC Starter", formula: "(Rake + Eave) / 100 round" },
  { key: "rhino", label: "Rolls Rhino Shield", formula: "1 roll / 10 sq round" },
  { key: "ice_water", label: "Rolls Ice and Water", formula: "(Valley/70) + (Flash + Step Fl)" },
  { key: "vent_sure", label: "Sticks Vent Sure", formula: "Ridge LF / 4" },
  { key: "pipe_jacks", label: "Pipe Jacks w/ Rain Collars", formula: "Check ins estimate" },
  { key: "osb", label: "Sheets OSB", formula: "1 sheet / 25 sq" },
  { key: "coil_nails", label: "Box Coil Nail Top Notch", formula: "1 box / 15 sq" },
  { key: "cap_nails", label: "Bucket Cap Nail", formula: "1 bucket / 30 sq" },
  { key: "np1", label: "Tubes NP-1 Black", formula: "3-4 per 10 sq" },
  { key: "gutter_apron", label: "Sticks Gutter Apron", colorField: true, formula: "Eaves / 10 + 10%" },
  { key: "drip_edge", label: "Sticks Drip Edge", colorField: true, formula: "Rakes / 10 + 10%" },
  { key: "step_flashing", label: "Box Step Flashing", formula: "Step Flash LF / 100" },
  { key: "paint", label: "Cans Paint", formula: "2-3 flat" },
];

/* EagleView measurement labels */
const EV_ROWS: Array<{ key: keyof PMEagleview; label: string }> = [
  { key: "sq_with_waste", label: "Square w/ waste" },
  { key: "ridges", label: "Ridges" },
  { key: "hips", label: "Hips" },
  { key: "valleys", label: "Valleys" },
  { key: "rakes", label: "Rakes" },
  { key: "eaves", label: "Eaves" },
  { key: "flashing", label: "Flashing" },
  { key: "step_flashing", label: "Step Flashing" },
];

const CHECKLIST_ITEMS: Array<{ key: keyof PMSiteChecklist; label: string }> = [
  { key: "satellite", label: "Satellite" },
  { key: "gutter_guards", label: "Gutter Guards" },
  { key: "pest_control", label: "Pest Control" },
  { key: "skylights", label: "Skylights" },
  { key: "pool", label: "Pool" },
];

const CHECKLIST_OPTIONS = ["", "No", "Trash", "Discard", "Yes"];

const POST_BUILD_RETURNS: Array<{ key: keyof PMPostBuild; label: string }> = [
  { key: "duration_return", label: "Duration" },
  { key: "proedge_return", label: "ProEdge" },
  { key: "starter_return", label: "Starter" },
  { key: "osb_return", label: "OSB" },
  { key: "rhino_return", label: "Rhino" },
  { key: "ice_water_return", label: "Ice Water" },
];

/* ═══════════════════════════════════════════════════════════════
   BUILD PHASE CLASSIFICATION
   ═══════════════════════════════════════════════════════════════ */
type BuildPhase = "pre_build" | "build_day" | "post_build" | "unscheduled" | "completed";

// Stages that mean the roof is done — these go to the Completed tab.
// Anything from ready_for_supps onward is "done with the build" from a PM
// perspective; trades / commission / payment / supps are tracked elsewhere.
// (roof_complete retired May 7, 2026 — kept here for legacy back-compat.)
const COMPLETED_STAGES = new Set<string>([
  "roof_complete",                       // legacy back-compat
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_claims",
  "supps_waiting_on_final_invoice",
  "trades_in_progress",
  "trades_complete",
  "collecting",
  "delinquent",
  "register_oc_warranty",
  "collect_payment",
  "commission_ready",
  "commission_approved",
  "commission_paid",
  "closed_won",
]);

type SheetFocus = "projected" | "installed" | null;

/* Square-count chip on a PM Board card. Empty fields say so (amber) so the
   PM knows to fill them in; tapping opens the workbook at that field. */
function SqChip({ label, value, title, onOpen }: { label: string; value: number; title: string; onOpen: () => void }) {
  const empty = !(value > 0);
  return (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpen(); } }}
      className={
        "text-[11px] px-2 py-0.5 rounded font-semibold cursor-pointer hover:ring-2 hover:ring-offset-1 " +
        (empty ? "bg-amber-50 text-amber-700 border border-dashed border-amber-400 hover:ring-amber-300"
               : "bg-slate-800 text-white hover:ring-slate-400")
      }
    >
      {label}: {empty ? "empty" : `${value} sq`}
    </span>
  );
}

function classifyBuildPhase(d: Deal): BuildPhase {
  if (d.stage && COMPLETED_STAGES.has(d.stage)) return "completed";
  if (!d.build_scheduled_date) return "unscheduled";
  // Local day, not UTC (8 PM ET is still today). A multi-day build is
  // "build day" on every day from its first to its last (Oct 26, 2026).
  const todayStr = todayISO();
  const bd = d.build_scheduled_date.slice(0, 10);
  const last = normalizeBuildEnd(bd, d.build_end_date) ?? bd;
  if (bd > todayStr) return "pre_build";
  if (last >= todayStr) return "build_day";
  return "post_build";
}

const PHASE_STYLE: Record<BuildPhase, { border: string; bg: string; label: string; dot: string }> = {
  pre_build:   { border: "border-l-amber-500",   bg: "bg-amber-50/40",   label: "Pre-Build",   dot: "bg-amber-500" },
  build_day:   { border: "border-l-emerald-500",  bg: "bg-emerald-50/40",  label: "Build Day",   dot: "bg-emerald-500" },
  post_build:  { border: "border-l-blue-500",     bg: "bg-blue-50/40",     label: "Post-Build",  dot: "bg-blue-500" },
  unscheduled: { border: "border-l-slate-300",    bg: "bg-white",          label: "Unscheduled", dot: "bg-slate-400" },
  completed:   { border: "border-l-slate-400",    bg: "bg-slate-50/50",    label: "Completed",   dot: "bg-slate-500" },
};

/* ═══════════════════════════════════════════════════════════════
   DEFAULT / EMPTY WORKBOOK
   ═══════════════════════════════════════════════════════════════ */
function emptyWorkbook(): PMWorkbook {
  return {
    eagleview: { sq_with_waste: 0, ridges: 0, hips: 0, valleys: 0, rakes: 0, eaves: 0, flashing: 0, step_flashing: 0 },
    qty_duration: 0, qty_proedge: 0, qty_starter: 0, qty_rhino: 0, qty_ice_water: 0,
    qty_vent_sure: 0, qty_pipe_jacks: 0, qty_osb: 0, qty_coil_nails: 0, qty_cap_nails: 0,
    qty_np1: 0, qty_gutter_apron: 0, qty_drip_edge: 0, qty_step_flashing: 0, qty_paint: 0,
    paint_color: "Black",
    extra_materials: [],
    post_build: {
      bundles_ordered: 0, additional_bundles: 0, total_leftover: 0,
      total_bundle_count: 0, total_square_count: 0,
      duration_return: 0, proedge_return: 0, starter_return: 0,
      osb_return: 0, rhino_return: 0, ice_water_return: 0,
      crew_additional_work_amount: 0, crew_additional_work_description: "",
    },
    site_checklist: { satellite: "", gutter_guards: "", pest_control: "", skylights: "", pool: "" },
    special_instructions: [],
    punch_list: [],
    drop_date: "",
    drop_notes: "",
    claim_info: "",
    rs_name: "",
    total_material_cost: 0,
    leftover_credit: 0,
    net_material_cost: 0,
  };
}

/* ═══════════════════════════════════════════════════════════════
   AUTO-CALCULATE material quantities from EagleView
   ═══════════════════════════════════════════════════════════════ */
function calcQuantities(ev: PMEagleview): Record<string, number> {
  return {
    duration: Math.ceil(ev.sq_with_waste * 3 + 4),
    proedge: Math.ceil((ev.hips + ev.ridges) / 25),
    starter: Math.ceil((ev.rakes + ev.eaves) / 100),
    rhino: Math.round(ev.sq_with_waste / 10),
    ice_water: Math.ceil((ev.valleys / 70) + ev.flashing + ev.step_flashing),
    vent_sure: Math.ceil(ev.ridges / 4),
    pipe_jacks: 0, // manual — from insurance estimate
    osb: Math.ceil(ev.sq_with_waste / 25),
    coil_nails: Math.ceil(ev.sq_with_waste / 15),
    cap_nails: Math.ceil(ev.sq_with_waste / 30),
    np1: Math.ceil((ev.sq_with_waste / 10) * 3.5),
    gutter_apron: Math.ceil((ev.eaves / 10) * 1.1),
    drip_edge: Math.ceil((ev.rakes / 10) * 1.1),
    step_flashing: Math.ceil(ev.step_flashing / 100),
    paint: 3,
  };
}

/* ═══════════════════════════════════════════════════════════════
   PM BOARD TAB — top-level component
   ═══════════════════════════════════════════════════════════════ */
interface PMBoardTabProps {
  initialDealId?: string | null;
  onClearInitial?: () => void;
}

type DateFilter = "all" | "today" | "week" | "month" | "quarter" | "ytd";

const DATE_FILTERS: Array<{ key: DateFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "ytd", label: "YTD" },
];

function getDateRange(filter: DateFilter): { start: string; end: string } | null {
  if (filter === "all") return null;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

  if (filter === "today") {
    const s = fmt(now);
    return { start: s, end: s };
  }
  if (filter === "week") {
    const day = now.getDay(); // 0=Sun
    const mon = new Date(y, m, d - ((day + 6) % 7)); // Monday
    const sun = new Date(y, m, d + (7 - day) % 7); // Sunday
    return { start: fmt(mon), end: fmt(sun) };
  }
  if (filter === "month") {
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    return { start: fmt(start), end: fmt(end) };
  }
  if (filter === "quarter") {
    const q = Math.floor(m / 3);
    const start = new Date(y, q * 3, 1);
    const end = new Date(y, q * 3 + 3, 0);
    return { start: fmt(start), end: fmt(end) };
  }
  // ytd
  return { start: `${y}-01-01`, end: fmt(now) };
}

export default function PMBoardTab({ initialDealId, onClearInitial }: PMBoardTabProps = {}) {
  const [builds, setBuilds] = useState<Deal[] | null>(null);
  const [members, setMembers] = useState<Record<string, TeamMember>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetFocus, setSheetFocus] = useState<SheetFocus>(null);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [err, setErr] = useState<string | null>(null);
  // Builds called off with no new date yet (Reschedule build, Oct 16, 2026) —
  // they fall under Unscheduled and need a chip saying why.
  const [callOffs, setCallOffs] = useState<Record<string, BuildReschedule>>({});

  // Auto-select deal when navigating from Schedule Build
  useEffect(() => {
    if (initialDealId && builds) {
      setSelectedId(initialDealId);
      if (onClearInitial) onClearInitial();
    }
  }, [initialDealId, builds, onClearInitial]);

  const load = useCallback(async () => {
    const cols = "id,homeowner_name,property_street,property_city,property_state,property_zip," +
      "claim_number,insurance_company,shingle_color,drip_edge_color,owner_id,split_rm_id," +
      "build_scheduled_date,build_end_date,ho_preferred_build_date,stage,phase,total_squares,installed_squares," +
      "hover_link,scope_document_url,pm_workbook,punch_list_items,adjuster_name,acv_amount,deductible," +
      "pre_inspection_data,pre_inspection_findings,pre_inspection_completed,build_notes,items_not_performing_detail," +
      "upgrades,retail_profit,at_cost_retail,upgrades_description,upgrades_internal_note,retail_profit_description,retail_profit_internal_note,at_cost_retail_description,at_cost_retail_internal_note";

    const [{ data, error: e1 }, { data: roster }] = await Promise.all([
      supabase.from("deals").select(cols)
        .in("phase", ["builds", "closing"])
        .order("ho_preferred_build_date", { ascending: true, nullsFirst: false }),
      supabase.from("team_roster").select("id,full_name,email,role,is_active"),
    ]);

    if (e1) { setErr(e1.message); return; }
    setBuilds((data ?? []) as unknown as Deal[]);
    const m: Record<string, TeamMember> = {};
    (roster ?? []).forEach((r: Record<string, unknown>) => { m[r.id as string] = r as unknown as TeamMember; });
    setMembers(m);
    fetchOpenCallOffs(((data ?? []) as unknown as { id: string }[]).map((d) => d.id)).then(setCallOffs);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mirror DealDetail edits in real time — any UPDATE on `deals` reloads the board
  // (and therefore the PMSheet's `deal` prop), so build_notes / punch_list_items / pm_workbook
  // edits made on the deal detail page surface here without manual refresh.
  useRealtimeTable("deals", load);
  useRealtimeTable("build_reschedules", load);

  const filtered = useMemo(() => {
    if (!builds) return [];
    const q = search.toLowerCase();
    const range = getDateRange(dateFilter);
    return builds.filter((d) => {
      // Text search
      if (q && !(
        d.homeowner_name?.toLowerCase().includes(q) ||
        d.property_street?.toLowerCase().includes(q) ||
        d.claim_number?.toLowerCase().includes(q)
      )) return false;
      // Date filter: any build day inside the range (a multi-day build counts on each day)
      if (range) {
        const bd = d.build_scheduled_date;
        if (!bd) return false; // no date = excluded when filtering by date
        const dateOnly = bd.slice(0, 10);
        const last = normalizeBuildEnd(dateOnly, d.build_end_date) ?? dateOnly;
        if (last < range.start || dateOnly > range.end) return false;
      }
      return true;
    });
  }, [builds, search, dateFilter]);

  const selected = builds?.find((d) => d.id === selectedId) ?? null;

  // ── Build phase classification (must be before conditional returns — Rules of Hooks) ──
  const [phaseFilter, setPhaseFilter] = useState<"all" | "pre_build" | "build_day" | "post_build" | "unscheduled" | "completed">("all");

  const phaseCounts = useMemo(() => {
    const counts = { all: 0, pre_build: 0, build_day: 0, post_build: 0, unscheduled: 0, completed: 0 };
    filtered.forEach((d) => { counts[classifyBuildPhase(d)]++; });
    // "All" tab shows everything except completed, so its count reflects that.
    counts.all = filtered.length - counts.completed;
    return counts;
  }, [filtered]);

  const phaseFiltered = useMemo(() => {
    if (phaseFilter === "all") {
      // "All" means active builds — exclude completed (those have their own tab).
      return filtered.filter((d) => classifyBuildPhase(d) !== "completed");
    }
    return filtered.filter((d) => classifyBuildPhase(d) === phaseFilter);
  }, [filtered, phaseFilter]);

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded-xl">{err}</div>;
  if (!builds) return <div className="text-slate-500 p-4">Loading PM Board…</div>;

  /* ── DEAL LIST (no deal selected) ────────────────────────── */
  if (!selected) {
    return (
      <div>
        {/* Date filter bar */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {DATE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setDateFilter(f.key)}
              className={
                "px-3 py-1.5 text-xs font-medium rounded-lg transition " +
                (dateFilter === f.key
                  ? "bg-bardin-teal text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-bardin-teal/40")
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search builds…"
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          <span className="text-xs text-slate-400">{filtered.length} builds</span>
        </div>

        {/* Build phase filter pills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {([
            { key: "all" as const, label: "All Active", count: phaseCounts.all, dot: "bg-slate-500" },
            { key: "pre_build" as const, label: "Pre-Build", count: phaseCounts.pre_build, dot: "bg-amber-500" },
            { key: "build_day" as const, label: "Build Day", count: phaseCounts.build_day, dot: "bg-emerald-500" },
            { key: "post_build" as const, label: "Post-Build", count: phaseCounts.post_build, dot: "bg-blue-500" },
            { key: "unscheduled" as const, label: "Unscheduled", count: phaseCounts.unscheduled, dot: "bg-slate-400" },
            { key: "completed" as const, label: "Completed", count: phaseCounts.completed, dot: "bg-slate-500" },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setPhaseFilter(f.key)}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-xl border transition " +
                (phaseFilter === f.key
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
              }
            >
              <span className={"w-2 h-2 rounded-full " + (phaseFilter === f.key ? "bg-white" : f.dot)} />
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {phaseFiltered.map((d) => {
            const rm = d.owner_id ? members[d.owner_id]?.full_name : "—";
            const hasPM = !!d.pm_workbook;
            const projSq = Number((d.pm_workbook as PMWorkbook | null)?.eagleview?.sq_with_waste ?? 0);
            const instSq = Number((d as Deal & { installed_squares?: number | null }).installed_squares ?? 0);
            const bp = classifyBuildPhase(d);
            const style = PHASE_STYLE[bp];
            return (
              <button
                key={d.id}
                onClick={() => { setSheetFocus(null); setSelectedId(d.id); }}
                className={
                  "rounded-xl shadow-sm p-4 text-left hover:shadow-md transition border-l-[4px] " +
                  style.border + " " + style.bg
                }
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={"w-2 h-2 rounded-full shrink-0 " + style.dot} />
                  <span className="font-semibold text-bardin-ink truncate">{d.homeowner_name}</span>
                  <span className={"text-[9px] font-bold px-1.5 py-0.5 rounded ml-auto shrink-0 " + (
                    bp === "pre_build" ? "bg-amber-100 text-amber-700" :
                    bp === "build_day" ? "bg-emerald-100 text-emerald-700" :
                    bp === "post_build" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-500"
                  )}>
                    {style.label}
                  </span>
                  {hasPM && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded shrink-0">PM ✓</span>}
                </div>
                <div className="flex justify-end gap-1.5 mt-1 mb-1">
                  <SqChip label="Projected" value={projSq} title="Squares w/ waste from EagleView — tap to review or edit"
                    onOpen={() => { setSheetFocus("projected"); setSelectedId(d.id); }} />
                  <SqChip label="Installed" value={instSq} title="Squares actually installed — tap to review or edit"
                    onOpen={() => { setSheetFocus("installed"); setSelectedId(d.id); }} />
                </div>
                <div className="text-xs text-slate-500 truncate">{isAddressLinkable(d) ? (<a href={mapsHref(d)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{d.property_street}, {d.property_city}</a>) : (<>{d.property_street}, {d.property_city}</>)}</div>
                <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                  {d.build_scheduled_date && (
                    <span className={
                      "px-2 py-0.5 rounded font-medium " + (
                        bp === "build_day" ? "bg-emerald-100 text-emerald-800" :
                        bp === "post_build" ? "bg-blue-100 text-blue-700" :
                        bp === "completed" ? "bg-slate-100 text-slate-600" :
                        "bg-amber-100 text-amber-700"
                      )
                    }>
                      {bp === "build_day" ? `🔨 TODAY${buildDayLabel(d.build_scheduled_date, d.build_end_date, todayISO()) ? ` · ${buildDayLabel(d.build_scheduled_date, d.build_end_date, todayISO())}` : ""}` :
                       bp === "post_build" ? "Built " :
                       bp === "completed" ? "✓ Built " :
                       "Build: "}
                      {bp !== "build_day" && fmtBuildSpanCompact(d.build_scheduled_date, d.build_end_date)}
                    </span>
                  )}
                  {callOffs[d.id] && !d.build_scheduled_date && (
                    <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-medium" title={REASON_LABEL[callOffs[d.id].reason_key] ?? callOffs[d.id].reason_key}>
                      ⛔ Called off {new Date(callOffs[d.id].created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · needs a date
                    </span>
                  )}
                  {!d.build_scheduled_date && d.ho_preferred_build_date && (
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                      Pref: {new Date(d.ho_preferred_build_date + "T12:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                  {d.shingle_color && <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{d.shingle_color}</span>}
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{rm}</span>
                </div>
              </button>
            );
          })}
        </div>
        {phaseFiltered.length === 0 && (
          <div className="text-center py-12 text-slate-400">No builds found.</div>
        )}
      </div>
    );
  }

  /* ── PM SHEET (deal selected) ────────────────────────────── */
  return (
    <PMSheet
      deal={selected}
      members={members}
      onBack={() => { setSelectedId(null); setSheetFocus(null); }}
      onSaved={load}
      focus={sheetFocus}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════
   PM SHEET — the spreadsheet-style form
   ═══════════════════════════════════════════════════════════════ */
function PMSheet({
  deal,
  members,
  onBack,
  onSaved,
  focus = null,
}: {
  deal: Deal;
  members: Record<string, TeamMember>;
  onBack: () => void;
  onSaved: () => void;
  focus?: SheetFocus;
}) {
  const { member, role, isSuperUser } = useAuth();
  /* Installed squares lives on deals.installed_squares (the column Job Analysis
     and Roof Master read) — saved on blur, separate from the workbook JSON. */
  const initialInstalled = (deal as Deal & { installed_squares?: number | null }).installed_squares;
  const [installedSq, setInstalledSq] = useState<string>(initialInstalled != null ? String(initialInstalled) : "");
  const [installedMsg, setInstalledMsg] = useState<string | null>(null);
  const saveInstalled = async () => {
    const trimmed = installedSq.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next != null && (!Number.isFinite(next) || next < 0)) { setInstalledMsg("Enter a number"); return; }
    if ((next ?? null) === (initialInstalled ?? null)) return;
    const { error } = await supabase.from("deals").update({ installed_squares: next }).eq("id", deal.id);
    setInstalledMsg(error ? `Couldn't save: ${error.message}` : "Saved");
    if (!error) onSaved();
  };
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => {
      const el = document.getElementById(focus === "projected" ? "pm-sq-projected" : "pm-sq-installed");
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); (el as HTMLInputElement).focus({ preventScroll: true }); }
    }, 150);
    return () => clearTimeout(t);
  }, [focus]);
  const canSeePricing = role !== "restore_manager" && role !== "team_lead" && role !== "asm";
  const [wb, setWb] = useState<PMWorkbook>(() =>
    deal.pm_workbook ? { ...emptyWorkbook(), ...deal.pm_workbook as PMWorkbook } : emptyWorkbook()
  );
  const [punchItems, setPunchItems] = useState<PunchListItem[]>(() =>
    Array.isArray(deal.punch_list_items) ? deal.punch_list_items as PunchListItem[] : []
  );
  const [buildNotes, setBuildNotes] = useState<string>(deal.build_notes ?? "");
  // Track last prop value so we can mirror remote edits (DealDetail save) without clobbering
  // local in-progress edits in this textarea.
  const lastDealBuildNotesRef = useRef<string>(deal.build_notes ?? "");
  useEffect(() => {
    const incoming = deal.build_notes ?? "";
    const lastSeen = lastDealBuildNotesRef.current;
    if (incoming === lastSeen) return; // no change from the server
    // Only adopt the incoming value if our local state matches what we last saw from props —
    // i.e. the user has NOT typed anything that hasn't been saved yet. If they have, leave their
    // edits in place (they'll win on next Save) and just update the ref so we re-evaluate next time.
    if (buildNotes === lastSeen) {
      setBuildNotes(incoming);
    }
    lastDealBuildNotesRef.current = incoming;
  }, [deal.build_notes, buildNotes]);
  /* Oct 1, 2026: the PM Board's SRS return email writes the return counts +
     total_leftover into pm_workbook.post_build server-side. Adopt them here
     when they change on the server, the same way build_notes does: only if
     the local value still equals the last server value (Robert hasn't typed
     an unsaved change), so an open sheet's next Save doesn't clobber them. */
  const RETURN_SYNC_KEYS = useMemo(() => [...POST_BUILD_RETURNS.map((r) => r.key), "total_leftover"] as Array<keyof PMPostBuild>, []);
  const incomingPb = ((deal.pm_workbook as PMWorkbook | null)?.post_build ?? {}) as Partial<PMPostBuild>;
  const incomingSig = RETURN_SYNC_KEYS.map((k) => Number(incomingPb[k]) || 0).join("|");
  const lastPbRef = useRef<string>(incomingSig);
  useEffect(() => {
    const lastSeen = lastPbRef.current.split("|");
    const incoming = incomingSig.split("|");
    if (incomingSig === lastPbRef.current) return;
    setWb((prev) => {
      const pb = { ...prev.post_build } as Record<string, unknown>;
      RETURN_SYNC_KEYS.forEach((k, i) => {
        if ((Number(pb[k]) || 0) === (Number(lastSeen[i]) || 0)) pb[k] = Number(incoming[i]) || 0;
      });
      return { ...prev, post_build: pb as unknown as PMPostBuild };
    });
    lastPbRef.current = incomingSig;
  }, [incomingSig, RETURN_SYNC_KEYS]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showCalModal, setShowCalModal] = useState(false);
  // Reschedule build (Oct 16, 2026): the Google event the CRM holds for this
  // build, and an open call-off (called off, no new date yet). Realtime so a
  // reschedule from the Today tab or another device shows here at once.
  const [showResched, setShowResched] = useState(false);
  const [calLink, setCalLink] = useState<BuildCalendarLink | null>(null);
  const [openCallOff, setOpenCallOff] = useState<BuildReschedule | null>(null);
  const refreshCalendar = useCallback(() => {
    fetchBuildCalendarLink(deal.id).then(setCalLink);
    fetchOpenCallOff(deal.id).then(setOpenCallOff);
  }, [deal.id]);
  useEffect(() => { refreshCalendar(); }, [refreshCalendar]);
  useRealtimeTable("build_calendar_events", refreshCalendar);
  useRealtimeTable("build_reschedules", refreshCalendar);
  // Schedule build (Oct 19, 2026): the latest Schedule build record. Its
  // first-payment plan shows as a chip, and it marks the build as scheduled.
  const [showSchedule, setShowSchedule] = useState(false);
  const [lastSchedule, setLastSchedule] = useState<BuildSchedule | null>(null);
  const refreshSchedule = useCallback(() => {
    fetchLatestSchedule(deal.id).then(setLastSchedule);
  }, [deal.id]);
  useEffect(() => { refreshSchedule(); }, [refreshSchedule]);
  useRealtimeTable("build_schedules", refreshSchedule);
  // Proof the material order went to SRS — survives forever on the job, so weeks
  // later Robert can still see it was sent and when. Realtime-backed so a send
  // from another device lights this up without a refresh.
  const [orderSend, setOrderSend] = useState<MaterialOrderSend | null>(null);
  const refreshOrderSend = useCallback(() => {
    fetchLatestOrderSend(deal.id).then(setOrderSend);
  }, [deal.id]);
  useEffect(() => { refreshOrderSend(); }, [refreshOrderSend]);
  useRealtimeTable("material_order_emails", refreshOrderSend);

  // A build counts as scheduled once it has a date AND made it onto the
  // calendar or to SRS (by Schedule build, or the old Add to Calendar way).
  // A date typed on the deal page alone doesn't count: nobody was told yet.
  const isScheduled = !!deal.build_scheduled_date && (!!calLink || orderSend?.send_status === "sent" || !!lastSchedule);
  const schedulable = SCHEDULE_FROM_STAGES.has(deal.stage ?? "");
  // One route (Oct 22, 2026): nothing is scheduled before the build is
  // approved on the Build Approvals board, so these stages get a pointer.
  const awaitingApproval = BEFORE_APPROVAL_STAGES.has(deal.stage ?? "");
  const showScheduleBtn = schedulable && !isScheduled && !openCallOff && canScheduleBuilds(role, isSuperUser);
  const showReschedBtn = schedulable && (isScheduled || !!openCallOff);
  const [newInstruction, setNewInstruction] = useState("");
  const [newPunchItem, setNewPunchItem] = useState("");
  const [newExtra, setNewExtra] = useState({ name: "", qty: 1, color: "", cost: 0 });

  const rmName = deal.owner_id ? members[deal.owner_id]?.full_name : "—";
  const splitRm = deal.split_rm_id ? members[deal.split_rm_id]?.full_name : null;

  /* ── helpers ───────────────────────────────────────────── */
  const setEv = (key: keyof PMEagleview, val: number) => {
    setWb((prev) => ({ ...prev, eagleview: { ...prev.eagleview, [key]: val } }));
  };

  const setQty = (key: string, val: number) => {
    setWb((prev) => ({ ...prev, [`qty_${key}`]: val }));
  };

  const setPostBuild = (key: keyof PMPostBuild, val: number | string) => {
    setWb((prev) => ({ ...prev, post_build: { ...prev.post_build, [key]: val } }));
  };

  const setChecklist = (key: keyof PMSiteChecklist, val: string) => {
    setWb((prev) => ({ ...prev, site_checklist: { ...prev.site_checklist, [key]: val } }));
  };

  /* Keep post_build.bundles_ordered, total_bundle_count, and total_square_count
     in sync with the formulas they're derived from. The Post-Build Tracking
     panel renders these live, but we also need them persisted in JSONB so
     downstream consumers (Finance Board roof-crew approval card, vendor
     receipt emails, QBO bills, audit log) can read canonical values without
     re-running the math.
        bundlesOrdered    = qty_duration + qty_proedge + qty_starter
        totalBundleCount  = bundlesOrdered + additional_bundles - total_leftover
        totalSquareCount  = totalBundleCount / 3   (one-decimal precision) */
  useEffect(() => {
    const bundlesOrdered =
      (Number(wb.qty_duration) || 0) +
      (Number(wb.qty_proedge) || 0) +
      (Number(wb.qty_starter) || 0);
    const totalBundleCount =
      bundlesOrdered +
      (Number(wb.post_build.additional_bundles) || 0) -
      (Number(wb.post_build.total_leftover) || 0);
    const totalSquareCount = Math.round((totalBundleCount / 3) * 10) / 10;

    const needsUpdate =
      (wb.post_build.bundles_ordered || 0) !== bundlesOrdered ||
      (wb.post_build.total_bundle_count || 0) !== totalBundleCount ||
      (wb.post_build.total_square_count || 0) !== totalSquareCount;

    if (needsUpdate) {
      setWb((prev) => ({
        ...prev,
        post_build: {
          ...prev.post_build,
          bundles_ordered: bundlesOrdered,
          total_bundle_count: totalBundleCount,
          total_square_count: totalSquareCount,
        },
      }));
    }
  }, [
    wb.qty_duration,
    wb.qty_proedge,
    wb.qty_starter,
    wb.post_build.additional_bundles,
    wb.post_build.total_leftover,
    wb.post_build.bundles_ordered,
    wb.post_build.total_bundle_count,
    wb.post_build.total_square_count,
  ]);

  /* Auto-calc from EagleView */
  const autoCalc = () => {
    const q = calcQuantities(wb.eagleview);
    setWb((prev) => ({
      ...prev,
      qty_duration: q.duration,
      qty_proedge: q.proedge,
      qty_starter: q.starter,
      qty_rhino: q.rhino,
      qty_ice_water: q.ice_water,
      qty_vent_sure: q.vent_sure,
      qty_osb: q.osb,
      qty_coil_nails: q.coil_nails,
      qty_cap_nails: q.cap_nails,
      qty_np1: q.np1,
      qty_gutter_apron: q.gutter_apron,
      qty_drip_edge: q.drip_edge,
      qty_step_flashing: q.step_flashing,
      qty_paint: q.paint,
    }));
  };

  /* Cost computation */
  const getQty = (key: string): number => (wb as unknown as Record<string, number>)[`qty_${key}`] ?? 0;
  const lineCost = (key: string) => getQty(key) * (PRICES[key] ?? 0);
  const standardTotal = MATERIAL_ROWS.reduce((sum, r) => sum + lineCost(r.key), 0);
  const extrasTotal = wb.extra_materials.reduce((sum, e) => sum + (e.qty * (e.cost ?? 0)), 0);
  const totalMaterialCost = standardTotal + extrasTotal;
  const returnTotal = POST_BUILD_RETURNS.reduce((sum, r) => sum + (wb.post_build[r.key] as number ?? 0), 0);

  /* Build the formatted calendar description from PM Board data */
  const buildCalendarDescription = (dropDate: string, dropNotes: string, buildDate: string, buildEndDate?: string | null): string => {
    const getQ = (key: string): number => (wb as unknown as Record<string, number>)[`qty_${key}`] ?? 0;

    // Material lines
    const materialLines = MATERIAL_ROWS.map((row) => {
      const q = getQ(row.key);
      let color = "";
      if (row.key === "duration") color = deal.shingle_color || "";
      else if (row.key === "proedge") color = deal.shingle_color ? "Match" : "";
      else if (row.key === "gutter_apron" || row.key === "drip_edge") color = deal.drip_edge_color || "Black";
      else if (row.key === "paint") color = wb.paint_color || "Black";
      return `${q}    ${row.label}    ${color}`.trimEnd();
    });

    // Extra materials
    const extraLines = wb.extra_materials
      .filter((e) => e.name)
      .map((e) => `${e.qty}    ${e.name}    ${e.color || ""}`);

    // Special instructions: the list on this sheet, then every line of the
    // deal's build notes the list doesn't already say. Greg, Oct 19, 2026
    // (Henslee): "make sure special instructions are added" — his "Replace
    // satellite" and "Remove old direct tv bracket" lived only in build notes.
    const allInstructions = mergeSpecialInstructions(wb.special_instructions, buildNotes);
    const instructionLines = allInstructions.length > 0
      ? ["", "    Special Instructions", ...allInstructions.map((s) => `*    ${s}`)]
      : [];

    // Contact
    const contactLine = "    Robert Ayers 404-542-6977";

    // Claim info
    const claimLine = wb.claim_info || deal.claim_number || "";

    // Drop / Build dates
    const fmtDate = (d: string) => {
      if (!d) return "";
      const dt = new Date(d + "T12:00:00");
      return dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    };
    const dropStr = dropDate ? fmtDate(dropDate) : "";
    // A multi-day build reads "Tuesday, October 27 - Wednesday, October 28"
    // (the same line build-calendar rewrites on a reschedule).
    const lastDay = buildDate ? normalizeBuildEnd(buildDate, buildEndDate) : null;
    const buildStr = buildDate ? (lastDay ? `${fmtDate(buildDate)} - ${fmtDate(lastDay)}` : fmtDate(buildDate)) : "";

    // Checklist
    const checklistLines = CHECKLIST_ITEMS.map((c) => {
      const val = wb.site_checklist[c.key] || "No";
      return `${val}    ${c.label}`;
    });

    const standardReqs = [
      "",
      "    Vendor: SRS",
      "",
      "    LEAVE EXTRA MATERIALS ON PALLET",
      "    Ice and water around all flashing, valleys and around chimney. Make sure",
      "    to protect landscaping and use ladder stabilizers. Protect Decks and move patio furniture if needed. Crew must use rope and",
      "    harness, clean gutters and blow driveway. Crew cannot wear shirts with",
      "    other roofing company's logo. Install ridge vent on all ridges. Please roll driveway and",
      "    entire jobsite with magnet. MUST BRING DUMP TRAILER!",
    ];

    return [
      ...materialLines,
      ...extraLines,
      ...instructionLines,
      "",
      "",
      contactLine,
      "",
      `Claim Info:    ${claimLine}`,
      "",
      "",
      `Drop :    ${dropStr}    ${dropNotes || "Early AM"}`,
      `Build :    ${buildStr}`,
      "",
      ...checklistLines,
      ...standardReqs,
    ].join("\n");
  };

  /* Save to Supabase */
  const save = async () => {
    setSaving(true);
    const toSave: PMWorkbook = {
      ...wb,
      total_material_cost: totalMaterialCost,
      leftover_credit: returnTotal,
      net_material_cost: totalMaterialCost - returnTotal,
    };
    await supabase.from("deals").update({
      pm_workbook: toSave,
      punch_list_items: punchItems,
      build_notes: buildNotes.trim() ? buildNotes : null,
    }).eq("id", deal.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved();
  };

  /* ── RENDER ────────────────────────────────────────────── */
  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between bg-cyan-100 rounded-xl px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm text-bardin-teal hover:underline">← Back</button>
          <h2 className="text-lg font-bold text-bardin-ink">Project Manager Sheet</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {saved && <span className="text-xs text-green-600 font-medium">✓ Saved</span>}
          {orderSend && (
            orderSend.send_status === "sent" ? (
              <span
                className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-2.5 py-1 text-xs font-medium"
                title={`Sent to ${orderSend.recipient_email}${orderSend.cc_emails?.length ? ` · cc ${orderSend.cc_emails.join(", ")}` : ""}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                Order sent to SRS · {fmtSentStamp(orderSend.sent_at)}
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-700 rounded-lg px-2.5 py-1 text-xs font-medium"
                title={orderSend.error_message ?? "The order email did not go out."}
              >
                ⚠ Order to SRS failed · {fmtSentStamp(orderSend.sent_at)}
              </span>
            )
          )}
          {calLink?.html_link && (
            <a
              href={calLink.html_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-blue-100"
              title={calLink.summary ?? "Open the Google Calendar event"}
            >
              📅 On Google Calendar ↗
            </a>
          )}
          {lastSchedule && (
            <span
              className={
                "inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium border " +
                (lastSchedule.payment_plan ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-700")
              }
              title={
                lastSchedule.payment_plan
                  ? `${lastSchedule.first_payment_label ?? "First payment"}${lastSchedule.first_payment_due ? ` ${fmtUSD(lastSchedule.first_payment_due)}` : ""} not in when scheduled ${new Date(lastSchedule.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}${lastSchedule.payment_plan_note ? `. Note: ${lastSchedule.payment_plan_note}` : ""}`
                  : "The first payment was in when this build was scheduled"
              }
            >
              💵 {lastSchedule.payment_plan ? PAYMENT_PLAN_LABELS[lastSchedule.payment_plan] : "First payment in"}
            </span>
          )}
          {showReschedBtn && (
            <button
              onClick={() => setShowResched(true)}
              className="bg-amber-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-600 flex items-center gap-1.5"
            >
              🗓 {openCallOff && !deal.build_scheduled_date ? "Set new build date" : "Reschedule build"}
            </button>
          )}
          {showScheduleBtn ? (
            <button
              onClick={async () => { await save(); setShowSchedule(true); }}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              🗓 Schedule build
            </button>
          ) : awaitingApproval ? (
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-900"
              title={deal.stage === "install_meeting"
                ? "The RM (or Chad) submits the build; Chad or Robert approves it on the Build Approvals board; then it can be scheduled here."
                : "Approve it on the Build Approvals board first, then schedule it here."}
            >
              ⏳ {deal.stage === "install_meeting" ? "Not submitted for approval yet" : "Waiting on build approval"}
              {deal.stage === "build_submitted" && canApproveBuilds(role, isSuperUser) && (
                <Link to={buildApprovalsLink(role)} className="font-semibold underline">Approve →</Link>
              )}
            </span>
          ) : (
            <button
              onClick={async () => { await save(); setShowCalModal(true); }}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              📅 {orderSend?.send_status === "sent" ? "Calendar / resend order" : "Add to Calendar"}
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="bg-bardin-teal text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {openCallOff && !deal.build_scheduled_date && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex flex-wrap items-center gap-2">
          <span className="font-semibold">⛔ Called off {new Date(openCallOff.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          <span>
            · {REASON_LABEL[openCallOff.reason_key] ?? openCallOff.reason_key}
            {openCallOff.cancelled_on_site_at ? " · cancelled on site" : ""}
            {openCallOff.old_build_date ? ` · was ${fmtShortDate(openCallOff.old_build_date)}` : ""}
            {" · no new date yet"}
          </span>
          <button onClick={() => setShowResched(true)} className="ml-auto bg-rose-600 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-rose-700">
            Pick a date
          </button>
        </div>
      )}

      {/* Job header row */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Homeowner</div>
            <div className="font-semibold text-bardin-ink">
              <DealLink id={deal.id}>{deal.homeowner_name}</DealLink>
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Address</div>
            <div className="font-medium">{isAddressLinkable(deal) ? (<a href={mapsHref(deal)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-orange-600">{deal.property_street}, {deal.property_city}</a>) : (<>{deal.property_street}, {deal.property_city}</>)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Sales</div>
            <div className="font-medium">{rmName}{splitRm ? ` / ${splitRm}` : ""}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">{normalizeBuildEnd(deal.build_scheduled_date?.slice(0, 10) ?? "", deal.build_end_date) ? "Build Dates" : "Build Date"}</div>
            <div className="font-medium">
              {deal.build_scheduled_date
                ? (normalizeBuildEnd(deal.build_scheduled_date.slice(0, 10), deal.build_end_date)
                    ? `${fmtBuildSpan(deal.build_scheduled_date, deal.build_end_date)} (${buildDayCount(deal.build_scheduled_date.slice(0, 10), deal.build_end_date)} days)`
                    : new Date(deal.build_scheduled_date + "T12:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }))
                : "—"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Claim Info</div>
            <input
              value={wb.claim_info || deal.claim_number || ""}
              onChange={(e) => setWb((p) => ({ ...p, claim_info: e.target.value }))}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder={deal.insurance_company ?? "Claim info…"}
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Drop Date</div>
            <input
              value={wb.drop_date}
              onChange={(e) => setWb((p) => ({ ...p, drop_date: e.target.value }))}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="e.g. Early AM Monday"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Roof Spec</div>
            <input
              value={wb.rs_name}
              onChange={(e) => setWb((p) => ({ ...p, rs_name: e.target.value }))}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="RS name"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Drop Notes</div>
            <input
              value={wb.drop_notes}
              onChange={(e) => setWb((p) => ({ ...p, drop_notes: e.target.value }))}
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="Notes…"
            />
          </div>
        </div>
      </div>

      {/* PRE Findings — pinned ABOVE the workbook so the install crew sees
          gutter guards / siding rot / satellite Y-flags before walking the
          property on build day. */}
      <PreFindingsCard deal={deal} className="mb-4" />
      {/* Oct 30, 2026 — the carrier lines the homeowner kept (no money on it). */}
      <DoNotPerformCard items={readDoNotPerform(deal.items_not_performing_detail)} rmName={rmName} className="mb-4" />
      <AddOnNotesCard deal={deal} className="mb-4" />

      {/* ── MAIN 3-COLUMN LAYOUT ──────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* LEFT: Material Order (cols 1-6) */}
        <div className="lg:col-span-5 bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-yellow-100 px-3 py-2 flex items-center justify-between">
            <h3 className="font-bold text-sm text-bardin-ink">Material Order</h3>
            <span className="text-xs text-slate-500">Vendor: SRS</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-yellow-50 text-left">
                  <th className="px-2 py-1.5 w-10">Qty</th>
                  <th className="px-2 py-1.5">Product</th>
                  <th className="px-2 py-1.5 w-20">Formula</th>
                  {canSeePricing && <th className="px-2 py-1.5 w-16 text-right">Cost</th>}
                  {canSeePricing && <th className="px-2 py-1.5 w-20 text-right">Total</th>}
                </tr>
              </thead>
              <tbody>
                {MATERIAL_ROWS.map((row) => {
                  const q = getQty(row.key);
                  const cost = lineCost(row.key);
                  return (
                    <tr key={row.key} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          value={q || ""}
                          onChange={(e) => setQty(row.key, Number(e.target.value) || 0)}
                          className="w-12 border rounded px-1.5 py-0.5 text-center text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <div className="font-medium text-bardin-ink">{row.label}</div>
                        {row.colorField && row.key === "duration" && (
                          <div className="text-[10px] text-purple-600">{deal.shingle_color || "No color"}</div>
                        )}
                        {row.colorField && row.key !== "duration" && (
                          <div className="text-[10px] text-slate-400">{row.key === "gutter_apron" || row.key === "drip_edge" ? (deal.drip_edge_color || "Black") : ""}</div>
                        )}
                        {row.key === "paint" && (
                          <input
                            type="text"
                            value={wb.paint_color || ""}
                            onChange={(e) => setWb((p) => ({ ...p, paint_color: e.target.value }))}
                            placeholder="Color…"
                            className="mt-0.5 w-32 border border-slate-300 rounded px-1.5 py-0.5 text-[11px]"
                          />
                        )}
                      </td>
                      <td className="px-2 py-1 text-[10px] text-slate-400">{row.formula}</td>
                      {canSeePricing && <td className="px-2 py-1 text-right text-slate-500">${PRICES[row.key]?.toFixed(2)}</td>}
                      {canSeePricing && <td className="px-2 py-1 text-right font-medium">${cost.toFixed(2)}</td>}
                    </tr>
                  );
                })}

                {/* Extra materials */}
                {wb.extra_materials.map((ext, i) => (
                  <tr key={`ext-${i}`} className="border-t border-slate-100 bg-blue-50/30">
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        min={0}
                        value={ext.qty || ""}
                        onChange={(e) => {
                          const arr = [...wb.extra_materials];
                          arr[i] = { ...arr[i], qty: Number(e.target.value) || 0 };
                          setWb((p) => ({ ...p, extra_materials: arr }));
                        }}
                        className="w-12 border rounded px-1.5 py-0.5 text-center text-xs"
                      />
                    </td>
                    <td className="px-2 py-1" colSpan={canSeePricing ? 2 : 2}>
                      <div className="flex items-center gap-1">
                        <input
                          value={ext.name}
                          onChange={(e) => {
                            const arr = [...wb.extra_materials];
                            arr[i] = { ...arr[i], name: e.target.value };
                            setWb((p) => ({ ...p, extra_materials: arr }));
                          }}
                          className="flex-1 border rounded px-1.5 py-0.5 text-xs"
                          placeholder="Item name"
                        />
                        <button
                          onClick={() => {
                            const arr = wb.extra_materials.filter((_, j) => j !== i);
                            setWb((p) => ({ ...p, extra_materials: arr }));
                          }}
                          className="text-red-400 hover:text-red-600 text-xs px-1"
                        >✕</button>
                      </div>
                    </td>
                    {canSeePricing && (
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={ext.cost || ""}
                          onChange={(e) => {
                            const arr = [...wb.extra_materials];
                            arr[i] = { ...arr[i], cost: Number(e.target.value) || 0 };
                            setWb((p) => ({ ...p, extra_materials: arr }));
                          }}
                          className="w-16 border rounded px-1.5 py-0.5 text-right text-xs"
                        />
                      </td>
                    )}
                    {canSeePricing && <td className="px-2 py-1 text-right font-medium">${(ext.qty * (ext.cost ?? 0)).toFixed(2)}</td>}
                  </tr>
                ))}

                {/* Add extra material row */}
                <tr className="border-t border-slate-200">
                  <td className="px-1 py-1">
                    <input type="number" min={0} value={newExtra.qty || ""} onChange={(e) => setNewExtra((p) => ({ ...p, qty: Number(e.target.value) || 0 }))} className="w-12 border rounded px-1.5 py-0.5 text-center text-xs" />
                  </td>
                  <td className="px-2 py-1" colSpan={canSeePricing ? 2 : 1}>
                    <input value={newExtra.name} onChange={(e) => setNewExtra((p) => ({ ...p, name: e.target.value }))} className="w-full border rounded px-1.5 py-0.5 text-xs" placeholder="+ Add extra material…" />
                  </td>
                  {canSeePricing && (
                    <td className="px-1 py-1">
                      <input type="number" min={0} step={0.01} value={newExtra.cost || ""} onChange={(e) => setNewExtra((p) => ({ ...p, cost: Number(e.target.value) || 0 }))} className="w-16 border rounded px-1.5 py-0.5 text-right text-xs" />
                    </td>
                  )}
                  <td className="px-2 py-1 text-center">
                    <button
                      disabled={!newExtra.name}
                      onClick={() => {
                        if (!newExtra.name) return;
                        setWb((p) => ({ ...p, extra_materials: [...p.extra_materials, { ...newExtra }] }));
                        setNewExtra({ name: "", qty: 1, color: "", cost: 0 });
                      }}
                      className="text-bardin-teal text-xs font-medium disabled:opacity-30"
                    >Add</button>
                  </td>
                </tr>
              </tbody>
              {canSeePricing && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td colSpan={4} className="px-2 py-1.5 font-bold text-sm text-right">Total Material Cost</td>
                    <td className="px-2 py-1.5 font-bold text-sm text-right">${totalMaterialCost.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td colSpan={4} className="px-2 py-1 text-right text-slate-500">Leftover Credit</td>
                    <td className="px-2 py-1 text-right text-green-600">${returnTotal.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-slate-50 border-t border-slate-200">
                    <td colSpan={4} className="px-2 py-1.5 font-bold text-sm text-right">Net Cost</td>
                    <td className="px-2 py-1.5 font-bold text-sm text-right">${(totalMaterialCost - returnTotal).toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* MIDDLE: EagleView Measurements (cols 7-9) */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-blue-100 px-3 py-2 flex items-center justify-between">
              <h3 className="font-bold text-sm text-bardin-ink">EagleView</h3>
              <button onClick={autoCalc} className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700">
                Auto-Calc →
              </button>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {EV_ROWS.map((r) => (
                  <tr key={r.key} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium text-bardin-ink">{r.label}</td>
                    <td className="px-2 py-1.5 w-24">
                      <input
                        id={r.key === "sq_with_waste" ? "pm-sq-projected" : undefined}
                        type="number"
                        min={0}
                        step={0.1}
                        value={wb.eagleview[r.key] || ""}
                        onChange={(e) => setEv(r.key, Number(e.target.value) || 0)}
                        className="w-full border rounded px-2 py-0.5 text-right text-xs"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Calculated values below EagleView — bundle counts via calcQuantities (source of truth) */}
            {(() => {
              const q = calcQuantities(wb.eagleview);
              return (
                <div className="border-t-2 border-slate-200 px-3 py-2">
                  <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Calculated (bundles)</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span className="text-slate-500" title="(Sq w/waste × 3) + 4">Duration bundles</span>
                    <span className="text-right font-medium">{q.duration}</span>
                    <span className="text-slate-500" title="(Hip + Ridge LF) / 25, round up">ProEdge bundles</span>
                    <span className="text-right font-medium">{q.proedge}</span>
                    <span className="text-slate-500" title="(Rake + Eave LF) / 100, round up">Starter bundles</span>
                    <span className="text-right font-medium">{q.starter}</span>
                    <span className="text-slate-500">Ridge Vent</span>
                    <span className="text-right font-medium">{(wb.eagleview.ridges / 4).toFixed(1)}</span>
                    <span className="text-slate-500">Drip Edge</span>
                    <span className="text-right font-medium">{(wb.eagleview.rakes / 10 * 1.1).toFixed(1)}</span>
                    <span className="text-slate-500">Gutter Apron</span>
                    <span className="text-right font-medium">{(wb.eagleview.eaves / 10 * 1.1).toFixed(1)}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Site Checklist */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-amber-100 px-3 py-2">
              <h3 className="font-bold text-sm text-bardin-ink">Site Checklist</h3>
            </div>
            <div className="p-3 space-y-2">
              {CHECKLIST_ITEMS.map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <span className="text-xs font-medium text-bardin-ink">{item.label}</span>
                  <select
                    value={wb.site_checklist[item.key]}
                    onChange={(e) => setChecklist(item.key, e.target.value)}
                    className="border rounded px-2 py-0.5 text-xs w-24"
                  >
                    {CHECKLIST_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || "—"}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="bg-white rounded-xl shadow-sm p-3 space-y-1.5">
            {deal.hover_link && (
              <a href={deal.hover_link} target="_blank" rel="noopener noreferrer" className="text-xs text-bardin-teal hover:underline block">Hover Report →</a>
            )}
            {deal.scope_document_url && (
              <a href={deal.scope_document_url} target="_blank" rel="noopener noreferrer" className="text-xs text-bardin-teal hover:underline block">Insurance Scope →</a>
            )}
          </div>
        </div>

        {/* RIGHT: Post-Build Tracking + Punch List (cols 10-12) */}
        <div className="lg:col-span-4 space-y-4">

          {/* Post-Build Bundle Tracking */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-green-100 px-3 py-2">
              <h3 className="font-bold text-sm text-bardin-ink">Post-Build Tracking</h3>
            </div>
            <div className="p-3 space-y-3">
              {(() => {
                const bundlesOrdered =
                  (Number(wb.qty_duration) || 0) +
                  (Number(wb.qty_proedge) || 0) +
                  (Number(wb.qty_starter) || 0);
                const totalBundleCount =
                  bundlesOrdered + (wb.post_build.additional_bundles || 0) - (wb.post_build.total_leftover || 0);
                return (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <label
                        className="text-slate-400 text-[10px]"
                        title="Auto-calc: Bundles OC Duration + Bundles OC ProEdge + Bundles OC Starter"
                      >
                        Bundles Ordered <span className="text-slate-300">ⓘ</span>
                      </label>
                      <div className="border rounded px-2 py-1 text-xs bg-slate-50 font-medium">
                        {bundlesOrdered || 0}
                      </div>
                    </div>
                    <div>
                      <label className="text-slate-400 text-[10px]">Additional Bundles</label>
                      <input type="number" min={0} value={wb.post_build.additional_bundles || ""} onChange={(e) => setPostBuild("additional_bundles", Number(e.target.value) || 0)} className="w-full border rounded px-2 py-1 text-xs" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-[10px]">Total Leftover</label>
                      <input type="number" min={0} value={wb.post_build.total_leftover || ""} onChange={(e) => setPostBuild("total_leftover", Number(e.target.value) || 0)} className="w-full border rounded px-2 py-1 text-xs" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-[10px]">Total Bundle Count</label>
                      <div className="border rounded px-2 py-1 text-xs bg-slate-50 font-medium">
                        {totalBundleCount || 0}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-slate-400 text-[10px]">Total Square Count</label>
                      <div className="border rounded px-2 py-1 text-xs bg-slate-50 font-medium">
                        {((totalBundleCount / 3) || 0).toFixed(1)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label htmlFor="pm-sq-installed" className="text-slate-600 text-[10px] font-semibold">Installed Squares</label>
                      <div className="flex gap-2 items-center">
                        <input
                          id="pm-sq-installed"
                          type="number" min={0} step={0.1}
                          value={installedSq}
                          placeholder="empty — fill in"
                          onChange={(e) => { setInstalledSq(e.target.value); setInstalledMsg(null); }}
                          onBlur={saveInstalled}
                          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                          className={"w-full border rounded px-2 py-1 text-xs font-medium " + (installedSq.trim() === "" ? "border-amber-400 bg-amber-50" : "")}
                        />
                        {totalBundleCount > 0 && installedSq.trim() === "" && (
                          <button type="button" onClick={() => setInstalledSq(((totalBundleCount / 3) || 0).toFixed(1))}
                            className="text-[10px] text-blue-600 hover:underline whitespace-nowrap">use {((totalBundleCount / 3) || 0).toFixed(1)}</button>
                        )}
                      </div>
                      {installedMsg && <div className={"text-[10px] mt-0.5 " + (installedMsg === "Saved" ? "text-emerald-600" : "text-rose-600")}>{installedMsg}</div>}
                    </div>
                  </div>
                );
              })()}

              {/* Return credits per material */}
              <div>
                <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Return / Material Pickup (units)</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {POST_BUILD_RETURNS.map((r) => (
                    <div key={r.key} className="flex items-center gap-1">
                      <span className="text-slate-500 w-16 truncate">{r.label}</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={(wb.post_build[r.key] as number) || ""}
                        onChange={(e) => setPostBuild(r.key, Number(e.target.value) || 0)}
                        className="flex-1 border rounded px-1.5 py-0.5 text-right text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Crew Work — feeds Finance Board crew approval */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold text-amber-700 uppercase">Additional Crew Work</div>
                  <div className="text-[10px] text-amber-700/70 italic">→ Finance crew approval</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="col-span-1">
                    <label className="text-slate-500 text-[10px]">Amount ($)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={wb.post_build.crew_additional_work_amount || ""}
                      placeholder="0.00"
                      onChange={(e) => setPostBuild("crew_additional_work_amount", Number(e.target.value) || 0)}
                      className="w-full border rounded px-2 py-1 text-xs text-right"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-slate-500 text-[10px]">
                      Description {(wb.post_build.crew_additional_work_amount ?? 0) > 0 && <span className="text-amber-700">· required</span>}
                    </label>
                    <input
                      type="text"
                      value={wb.post_build.crew_additional_work_description ?? ""}
                      placeholder={(wb.post_build.crew_additional_work_amount ?? 0) > 0 ? "What was the extra work?" : "Optional"}
                      onChange={(e) => setPostBuild("crew_additional_work_description", e.target.value)}
                      className="w-full border rounded px-2 py-1 text-xs"
                    />
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 leading-snug">
                  Amount + description show up on the Finance Board crew-payment approval card.
                  Robert enters here once; Chad sees it pre-filled when approving Risol's payment.
                </div>
              </div>

              {/* Email Material Pick Up Request */}
              <button
                onClick={() => {
                  const addr = [deal.property_street, deal.property_city, deal.property_state].filter(Boolean).join(", ");
                  const name = deal.homeowner_name ?? "Customer";
                  const subject = `Material Pick Up Request - ${name} - ${addr} - Return / Material Pickup List`;

                  // Build return list for body
                  const returnLines = POST_BUILD_RETURNS
                    .filter((r) => (wb.post_build[r.key] as number) > 0)
                    .map((r) => `${r.label}: ${wb.post_build[r.key] as number}`);
                  const leftoverLine = wb.post_build.total_leftover
                    ? `Total Leftover Bundles: ${wb.post_build.total_leftover}`
                    : null;

                  const body = [
                    `Material Pick Up Request`,
                    ``,
                    `Customer: ${name}`,
                    `Address: ${addr}`,
                    deal.claim_number ? `Claim #: ${deal.claim_number}` : null,
                    ``,
                    `Return / Material Pickup List:`,
                    ...(returnLines.length > 0 ? returnLines : ["  (none entered yet)"]),
                    leftoverLine ? `\n${leftoverLine}` : null,
                    ``,
                    `Please schedule pickup at the above address.`,
                    ``,
                    `Thank you,`,
                    `Bardin Roofing`,
                  ].filter((l) => l !== null).join("\n");

                  // Recipients: SRS + RM + Robert
                  const rmEmail = deal.owner_id ? members[deal.owner_id]?.email : null;
                  const toList = ["ordersmarietta@srsbuildingproducts.com"];
                  const ccList: string[] = [];
                  if (rmEmail) ccList.push(rmEmail);
                  // Robert Ayers
                  const robert = Object.values(members).find((m) => m.full_name?.toLowerCase().includes("robert"));
                  if (robert?.email && robert.email !== rmEmail) ccList.push(robert.email);

                  const mailto = `mailto:${toList.join(",")}?cc=${encodeURIComponent(ccList.join(","))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  window.open(mailto, "_blank");
                }}
                className="w-full mt-2 bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-1.5"
              >
                <span>✉</span> Email Material Pick Up Request
              </button>
            </div>
          </div>

          {/* Build Notes (deal-level — also editable on Deal Detail) */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-amber-100 px-3 py-2 flex items-center justify-between">
              <h3 className="font-bold text-sm text-bardin-ink">Build Notes</h3>
              <span className="text-[10px] text-slate-500">
                {buildNotes.trim().length > 0 ? `${buildNotes.trim().length} chars` : "empty"}
              </span>
            </div>
            <div className="p-3">
              <textarea
                value={buildNotes}
                onChange={(e) => setBuildNotes(e.target.value)}
                placeholder="Crew instructions, gotchas, callouts for the install crew…"
                rows={4}
                className="w-full border rounded-lg px-2 py-1.5 text-xs leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <div className="text-[10px] text-slate-400 mt-1 italic">
                Saves with the rest of the PM sheet. Also visible on the deal detail.
              </div>
            </div>
          </div>

          {/* Punch List (deal-level — visible on Deal Detail too) */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-red-100 px-3 py-2 flex items-center justify-between">
              <h3 className="font-bold text-sm text-bardin-ink">Punch List</h3>
              <span className="text-[10px] text-slate-500">
                {punchItems.filter((p) => p.status === "open").length} open
                {punchItems.filter((p) => p.status === "complete").length > 0 &&
                  ` · ${punchItems.filter((p) => p.status === "complete").length} done`}
              </span>
            </div>
            <div className="p-3">
              {punchItems.length === 0 && (
                <div className="text-xs text-slate-400 italic mb-2">No punch list items</div>
              )}
              {punchItems.map((item, i) => (
                <div key={i} className={`flex items-start gap-2 mb-2 text-xs ${item.status === "complete" ? "opacity-50" : ""}`}>
                  <button
                    onClick={() => {
                      const arr = [...punchItems];
                      arr[i] = {
                        ...arr[i],
                        status: arr[i].status === "open" ? "complete" : "open",
                        completed_at: arr[i].status === "open" ? new Date().toISOString() : null,
                      };
                      setPunchItems(arr);
                    }}
                    className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      item.status === "complete"
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {item.status === "complete" && <span className="text-[9px]">✓</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={item.status === "complete" ? "line-through text-slate-400" : "text-bardin-ink"}>
                      {item.text}
                    </div>
                    {item.assigned_to_name && (
                      <div className="text-[10px] text-slate-400">Assigned: {item.assigned_to_name}</div>
                    )}
                    {item.notes && (
                      <div className="text-[10px] text-slate-400 italic">{item.notes}</div>
                    )}
                  </div>
                  <button
                    onClick={() => setPunchItems((prev) => prev.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 flex-shrink-0"
                  >✕</button>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-2">
                <input
                  value={newPunchItem}
                  onChange={(e) => setNewPunchItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newPunchItem.trim()) {
                      setPunchItems((prev) => [...prev, {
                        text: newPunchItem.trim(),
                        status: "open",
                        cost: null,
                        responsibility: null,
                        assigned_to: null,
                        assigned_to_name: null,
                        notes: "",
                        created_by: member?.id ?? null,
                        created_by_name: member?.full_name ?? null,
                        created_at: new Date().toISOString(),
                        completed_at: null,
                      }]);
                      setNewPunchItem("");
                    }
                  }}
                  className="flex-1 border rounded px-2 py-1 text-xs"
                  placeholder="+ Add punch list item…"
                />
                <button
                  disabled={!newPunchItem.trim()}
                  onClick={() => {
                    if (newPunchItem.trim()) {
                      setPunchItems((prev) => [...prev, {
                        text: newPunchItem.trim(),
                        status: "open",
                        cost: null,
                        responsibility: null,
                        assigned_to: null,
                        assigned_to_name: null,
                        notes: "",
                        created_by: member?.id ?? null,
                        created_by_name: member?.full_name ?? null,
                        created_at: new Date().toISOString(),
                        completed_at: null,
                      }]);
                      setNewPunchItem("");
                    }
                  }}
                  className="text-xs text-bardin-teal font-medium disabled:opacity-30"
                >Add</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SPECIAL INSTRUCTIONS ───────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-orange-100 px-3 py-2">
          <h3 className="font-bold text-sm text-bardin-ink">Special Instructions</h3>
        </div>
        <div className="p-3">
          {wb.special_instructions.map((inst, i) => (
            <div key={i} className="flex items-center gap-2 mb-1.5 text-xs">
              <span className="text-orange-500 font-bold">*</span>
              <span className="flex-1">{inst}</span>
              <button
                onClick={() => setWb((p) => ({ ...p, special_instructions: p.special_instructions.filter((_, j) => j !== i) }))}
                className="text-red-400 hover:text-red-600"
              >✕</button>
            </div>
          ))}
          <div className="flex items-center gap-2 mt-2">
            <input
              value={newInstruction}
              onChange={(e) => setNewInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newInstruction.trim()) {
                  setWb((p) => ({ ...p, special_instructions: [...p.special_instructions, newInstruction.trim()] }));
                  setNewInstruction("");
                }
              }}
              className="flex-1 border rounded px-2 py-1 text-xs"
              placeholder="+ Add instruction…"
            />
            <button
              disabled={!newInstruction.trim()}
              onClick={() => {
                if (newInstruction.trim()) {
                  setWb((p) => ({ ...p, special_instructions: [...p.special_instructions, newInstruction.trim()] }));
                  setNewInstruction("");
                }
              }}
              className="text-xs text-bardin-teal font-medium disabled:opacity-30"
            >Add</button>
          </div>
        </div>
      </div>

      {/* ── STANDARD BUILD NOTES (read-only) ──────────────── */}
      <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <div className="font-bold text-slate-400 uppercase text-[10px] mb-2">Standard Build Requirements</div>
        <div>LEAVE EXTRA MATERIALS ON PALLET</div>
        <div>Ice and water around all flashing, valleys and around chimney. Make sure to protect landscaping and use ladder stabilizers.</div>
        <div>Protect Decks and move patio furniture if needed. Crew must use rope and harness, clean gutters and blow driveway.</div>
        <div>Crew cannot wear shirts with other roofing company's logo. Install ridge vent on all ridges.</div>
        <div>Please roll driveway and entire jobsite with magnet. MUST BRING DUMP TRAILER!</div>
      </div>

      {/* ── CONTACT INFO (read-only) ──────────────────────── */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500 px-1">
        <span>Robert Ayers 404-542-6977</span>
        <span>Ramiro Perez 708-470-0910</span>
      </div>

      {/* ── ADD TO CALENDAR MODAL ────────────────────────── */}
      {showCalModal && (
        <AddToCalendarModal
          deal={deal}
          members={members}
          wb={wb}
          buildDescription={buildCalendarDescription}
          calLink={calLink}
          onCalendarChanged={refreshCalendar}
          onClose={() => setShowCalModal(false)}
          onSent={refreshOrderSend}
        />
      )}

      {/* ── SCHEDULE BUILD (Oct 19, 2026) ────────────────────── */}
      {showSchedule && (
        <PMScheduleBuildModal
          deal={deal}
          defaultTitle={defaultCalendarTitle(deal, members, wb)}
          guests={calendarGuests(deal, members)}
          location={calendarLocation(deal)}
          buildDescription={buildCalendarDescription}
          specialInstructions={mergeSpecialInstructions(wb.special_instructions, buildNotes)}
          initialDropDate={wb.drop_date || ""}
          initialDropNotes={wb.drop_notes || "Early AM"}
          lastOrder={orderSend}
          hasCalendarLink={!!calLink}
          onClose={() => setShowSchedule(false)}
          onDone={(r) => {
            // The server wrote the drop into pm_workbook; keep this sheet's
            // copy in step so Robert's next Save doesn't put the old one back.
            setWb((prev) => ({ ...prev, drop_date: r.deal.drop_date, drop_notes: r.deal.drop_notes || prev.drop_notes }));
            refreshCalendar();
            refreshOrderSend();
            refreshSchedule();
            onSaved();
          }}
        />
      )}

      {/* ── RESCHEDULE BUILD ─────────────────────────────────── */}
      {showResched && (
        <RescheduleBuildModal
          open
          deal={deal}
          openCallOff={openCallOff}
          onClose={() => setShowResched(false)}
          onDone={(r) => {
            // The server already wrote the new drop date into pm_workbook; keep
            // this sheet's copy in step so Robert's next Save doesn't put the
            // old one back.
            if (r.deal.new_build_date && r.deal.new_drop_date) {
              const nd = r.deal.new_drop_date;
              setWb((prev) => ({ ...prev, drop_date: nd, drop_notes: r.deal.drop_notes || prev.drop_notes }));
            }
            refreshCalendar();
            refreshOrderSend();
            onSaved();
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BUILD EVENT PIECES — shared by Add to Calendar and Schedule build
   so both put the same event on Team Alex Builds.
   ═══════════════════════════════════════════════════════════════ */

// Guests: SRS, Risol, R. Perez, ASM, TL, RM(s), Robert, Jennifer
const FIXED_GUESTS = [
  "ordersmarietta@srsbuildingproducts.com",
  "burroguate@gmail.com",
  "rperez@bardinroofing.com",
];

function calendarGuests(deal: Deal, members: Record<string, TeamMember>): string[] {
  const guestEmails = [...FIXED_GUESTS];
  // Add team members by role
  Object.values(members).forEach((m) => {
    if (!m.email || !m.is_active) return;
    if (m.role === "asm" || m.role === "roof_team_manager" || m.role === "qc" || m.role === "roof_director" || m.role === "director_of_operations") {
      if (!guestEmails.includes(m.email)) guestEmails.push(m.email);
    }
  });
  // Add RM(s) + TL
  if (deal.owner_id && members[deal.owner_id]?.email) {
    const e = members[deal.owner_id].email;
    if (!guestEmails.includes(e)) guestEmails.push(e);
    // Find TL for this RM
    const rm = members[deal.owner_id] as TeamMember & { team_lead_id?: string | null };
    if (rm.team_lead_id && members[rm.team_lead_id]?.email) {
      const tlEmail = members[rm.team_lead_id].email;
      if (!guestEmails.includes(tlEmail)) guestEmails.push(tlEmail);
    }
  }
  if (deal.split_rm_id && members[deal.split_rm_id]?.email) {
    const e = members[deal.split_rm_id].email;
    if (!guestEmails.includes(e)) guestEmails.push(e);
  }
  return guestEmails;
}

/** "Sq / Last Name (Bardin) RM1 and RM2 if split" — editable in both windows. */
function defaultCalendarTitle(deal: Deal, members: Record<string, TeamMember>, wb: PMWorkbook): string {
  const sq = wb.eagleview.sq_with_waste || deal.total_squares || "—";
  const fullName = deal.homeowner_name ?? "";
  const rmName = deal.owner_id ? members[deal.owner_id]?.full_name?.split(" ").pop() : "";
  const splitName = deal.split_rm_id ? members[deal.split_rm_id]?.full_name?.split(" ").pop() : "";
  return `${sq} / ${fullName} (Bardin) ${rmName}${splitName ? ` and ${splitName}` : ""}`;
}

function calendarLocation(deal: Deal): string {
  return [deal.property_street, deal.property_city, deal.property_state, deal.property_zip].filter(Boolean).join(", ");
}

/* ═══════════════════════════════════════════════════════════════
   ADD TO CALENDAR MODAL
   ═══════════════════════════════════════════════════════════════ */
function AddToCalendarModal({
  deal,
  members,
  wb,
  buildDescription,
  calLink,
  onCalendarChanged,
  onClose,
  onSent,
}: {
  deal: Deal;
  members: Record<string, TeamMember>;
  wb: PMWorkbook;
  buildDescription: (dropDate: string, dropNotes: string, buildDate: string, buildEndDate?: string | null) => string;
  /** The Google event the CRM already holds for this build, if any. */
  calLink: BuildCalendarLink | null;
  onCalendarChanged: () => void;
  onClose: () => void;
  onSent: () => void;
}) {
  const [buildDate, setBuildDate] = useState(deal.build_scheduled_date ?? "");
  // Oct 26, 2026: the last day of a multi-day build ("" = one day).
  const [buildEnd, setBuildEnd] = useState(
    deal.build_scheduled_date ? (normalizeBuildEnd(deal.build_scheduled_date.slice(0, 10), deal.build_end_date) ?? "") : "",
  );
  const endISO = buildDate ? normalizeBuildEnd(buildDate, buildEnd || null) : null;
  const endProblem = buildDate && buildEnd ? buildEndProblem(buildDate, buildEnd) : null;
  const pickStart = (d: string) => {
    const n = buildDate ? buildDayCount(buildDate, buildEnd || null) : 1;
    setBuildDate(d);
    setBuildEnd(d && n > 1 ? addWeekdaysISO(d, n - 1) : "");
  };
  // Oct 16, 2026: when the builds calendar is connected, the event is created
  // through the Google API right here (the CRM keeps its id, so Reschedule
  // build can edit it later). The template link stays as the fallback.
  const [calStatus, setCalStatus] = useState<CalendarStatus | null | "loading">("loading");
  const [creating, setCreating] = useState(false);
  const [calErr, setCalErr] = useState<string | null>(null);
  const [calResult, setCalResult] = useState<UpsertEventResult | null>(null);
  useEffect(() => {
    let alive = true;
    fetchCalendarStatus().then((s) => { if (alive) setCalStatus(s); }).catch(() => { if (alive) setCalStatus(null); });
    return () => { alive = false; };
  }, []);
  const calConfigured = calStatus !== "loading" && !!calStatus?.configured;
  // Drop defaults to the last weekday before the build when the workbook has
  // none (SRS Marietta delivers Mon–Fri only, so a Monday build drops the
  // Friday before — Greg, Oct 16, 2026). An order emailed to a supplier with
  // no delivery date is worse than one with an editable default. Still
  // editable right here before it sends.
  const [dropDate, setDropDate] = useState(
    wb.drop_date || srsDropDateISO(deal.build_scheduled_date),
  );
  const [dropNotes, setDropNotes] = useState(wb.drop_notes || "Early AM");

  // Two-step send. The Google Calendar button only OPENS a prefilled tab —
  // Robert still hits Save over there and Google never reports back. So the
  // order email waits for him to confirm he actually saved it; an abandoned
  // calendar event never produces a supplier order.
  const [phase, setPhase] = useState<"compose" | "confirm" | "done">("compose");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [result, setResult] = useState<SendOrderResult | null>(null);

  // Keep the drop date trailing the build date until the user edits it himself.
  const dropTouchedRef = useRef(false);
  useEffect(() => {
    if (dropTouchedRef.current || wb.drop_date) return;
    setDropDate(srsDropDateISO(buildDate));
  }, [buildDate, wb.drop_date]);

  const guestEmails = calendarGuests(deal, members);
  const [title, setTitle] = useState(() => defaultCalendarTitle(deal, members, wb));
  const eventLocation = calendarLocation(deal);

  /** Create (or refresh) the event through the API. Falls back to the
   *  template tab when the calendar isn't connected. */
  const addToCalendar = async () => {
    if (!buildDate) return;
    if (!calConfigured) { openCalendar(); return; }
    setCreating(true);
    setCalErr(null);
    try {
      const r = await buildCalendar<UpsertEventResult>({
        action: "upsert_event",
        deal_id: deal.id,
        title,
        description: buildDescription(dropDate, dropNotes, buildDate, endISO),
        location: eventLocation,
        build_date: buildDate,
        build_end_date: endISO,
        guests: guestEmails,
      });
      setCalResult(r);
      onCalendarChanged();
      setPhase("confirm");
    } catch (e) {
      if (e instanceof NotConfiguredError) { openCalendar(); return; }
      setCalErr((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const openCalendar = () => {
    if (!buildDate) return;

    const description = buildDescription(dropDate, dropNotes, buildDate, endISO);

    // Google Calendar URL — all-day event on the build date(s). Google's end
    // date is exclusive: the day after the last build day.
    // src=Team+Alex+Builds sets the calendar (user must have it)
    const dateStr = buildDate.replace(/-/g, "");
    const nextDay = new Date((endISO ?? buildDate) + "T12:00:00");
    nextDay.setDate(nextDay.getDate() + 1);
    const endStr = `${nextDay.getFullYear()}${String(nextDay.getMonth() + 1).padStart(2, "0")}${String(nextDay.getDate()).padStart(2, "0")}`;

    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: title,
      dates: `${dateStr}/${endStr}`,
      details: description,
      location: eventLocation,
    });
    // Add guests individually — Google Calendar uses &add= for each
    const guestParams = guestEmails.map((e) => `add=${encodeURIComponent(e)}`).join("&");
    const url = `https://calendar.google.com/calendar/render?${params.toString()}&${guestParams}`;

    window.open(url, "_blank");
    // Stay open and ask whether it actually saved — that answer is what
    // authorizes the supplier order.
    setPhase("confirm");
  };

  const sendOrder = async () => {
    setSendErr(null);
    setSending(true);
    try {
      const r = await sendMaterialOrder({
        dealId: deal.id,
        buildDate,
        dropDate,
        dropNotes,
      });
      setResult(r);
      setPhase("done");
      onSent();
    } catch (e) {
      setSendErr((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-bardin-ink">Add Build to Google Calendar</h3>
          <p className="text-xs text-slate-500">
            {deal.homeowner_name} · {deal.property_street} — Team Alex Builds
          </p>
        </div>

        {phase === "compose" && (
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Event Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-medium"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Build Day</label>
              <input
                type="date"
                value={buildDate}
                onChange={(e) => pickStart(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Build Day</label>
              <input
                type="date"
                value={buildEnd || buildDate}
                min={buildDate || undefined}
                disabled={!buildDate}
                onChange={(e) => setBuildEnd(e.target.value && e.target.value !== buildDate ? e.target.value : "")}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
              />
              {endProblem
                ? <p className="text-[11px] text-red-600 mt-1">{endProblem}</p>
                : <p className="text-[11px] text-slate-400 mt-1">Same as the first day for a one-day build.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Drop Date</label>
              <input
                type="date"
                value={dropDate}
                onChange={(e) => { dropTouchedRef.current = true; setDropDate(e.target.value); }}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-slate-400 mt-1">Defaults to the weekday before the first build day — Friday for a Monday build (SRS delivers Mon–Fri).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Drop Notes</label>
              <input
                type="text"
                value={dropNotes}
                onChange={(e) => setDropNotes(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                placeholder="e.g. Early AM"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Guests</label>
            <div className="border rounded px-3 py-2 text-xs text-slate-600 bg-slate-50 space-y-0.5 max-h-28 overflow-y-auto">
              {guestEmails.map((e) => <div key={e}>{e}</div>)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description Preview</label>
            <pre className="border rounded px-3 py-2 text-[10px] text-slate-600 bg-slate-50 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
              {buildDescription(dropDate, dropNotes, buildDate, endISO)}
            </pre>
          </div>

          {buildDate && deal.build_scheduled_date && (buildDate !== deal.build_scheduled_date.slice(0, 10) || endISO !== normalizeBuildEnd(deal.build_scheduled_date.slice(0, 10), deal.build_end_date)) && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
              These dates differ from the build dates on the deal ({fmtBuildSpan(deal.build_scheduled_date, deal.build_end_date)}). To move the build, use Reschedule build — the calendar, SRS and the homeowner all follow it.
            </div>
          )}

          {calErr && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {calErr}
              <button onClick={openCalendar} className="ml-2 underline">Open the Google template instead</button>
            </div>
          )}

          <p className="text-xs text-slate-500">
            {calConfigured
              ? <>{calLink ? "Updates the event already on Team Alex Builds" : "Creates an all-day event on Team Alex Builds"} and invites the guests. Material drop = the weekday before the build. Then you'll be asked to send the material order to SRS.</>
              : <>Opens Google Calendar with an all-day event on Team Alex Builds. Material drop = the weekday before the build.
                 Once you've saved the event, you'll be asked to send the material order to SRS.</>}
          </p>
        </div>
        )}

        {/* ── STEP 2: did it actually save? ──────────────────────────────
            Google gives us no callback, so this is the only honest signal
            that a build is really on the calendar. The supplier order is
            gated behind it. */}
        {phase === "confirm" && (
          <div className="p-4 space-y-3">
            {calResult ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-sm text-emerald-800">
                <span className="font-semibold">
                  {calResult.created ? "Event created on Team Alex Builds"
                    : calResult.adopted ? "Found the event already on Team Alex Builds and updated it"
                    : "Event updated on Team Alex Builds"}
                </span>
                {" · "}{calResult.guests.length} guest{calResult.guests.length === 1 ? "" : "s"} invited
                {calResult.html_link && <> · <a href={calResult.html_link} target="_blank" rel="noreferrer" className="underline">Open in Google ↗</a></>}
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 text-sm text-slate-700">
                Google Calendar opened in a new tab. Save the event there, then come back here.
              </div>
            )}

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Material order ready to send
              </div>
              <div className="px-3 py-2.5 text-sm space-y-1">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Job</span>
                  <span className="font-medium text-bardin-ink text-right">{deal.homeowner_name || "—"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Jobsite</span>
                  <span className="font-medium text-bardin-ink text-right">
                    {[deal.property_street, deal.property_city].filter(Boolean).join(", ") || "—"}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Build</span>
                  <span className="font-medium text-bardin-ink text-right">{buildDate ? fmtBuildSpan(buildDate, endISO) : "—"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Drop</span>
                  <span className="font-medium text-bardin-ink text-right">
                    {dropDate || "—"}{dropNotes ? ` · ${dropNotes}` : ""}
                  </span>
                </div>
                <div className="pt-1.5 mt-1.5 border-t border-slate-100 text-xs text-slate-500">
                  To ordersmarietta@srsbuildingproducts.com · cc rayers@bardinroofing.com
                </div>
              </div>
            </div>

            {sendErr && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{sendErr}</div>
            )}

            {!calResult && (
              <button
                onClick={openCalendar}
                className="text-xs text-blue-600 hover:underline"
              >
                Reopen Google Calendar
              </button>
            )}
          </div>
        )}

        {/* ── STEP 3: receipt ───────────────────────────────────────── */}
        {phase === "done" && result && (
          <div className="p-4 space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-600" />
                Order sent to SRS
              </div>
              <p className="text-xs text-emerald-700 mt-1.5">
                {result.line_count} material line{result.line_count === 1 ? "" : "s"} · sent {fmtSentStamp(result.sent_at)}
              </p>
            </div>
            <div className="text-xs text-slate-600 space-y-1">
              <div><span className="text-slate-400">To</span> {result.to}</div>
              <div><span className="text-slate-400">Cc</span> {result.cc.join(", ")}</div>
              <div className="pt-1 text-slate-500">{result.subject}</div>
            </div>
            <p className="text-xs text-slate-500">
              A copy is in your inbox, and this job now shows a sent stamp on the PM Board.
            </p>
          </div>
        )}

        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
          {phase === "compose" && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800">Cancel</button>
              <button
                onClick={addToCalendar}
                disabled={!buildDate || !!endProblem || creating || calStatus === "loading"}
                className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? "Creating event…" : calStatus === "loading" ? "Checking calendar…" : calConfigured ? (calLink ? "Update calendar event" : "Create on Google Calendar") : "Open in Google Calendar"}
              </button>
            </>
          )}
          {phase === "confirm" && (
            <>
              <button
                onClick={onClose}
                disabled={sending}
                className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                {calResult ? "Not now" : "Didn't save it"}
              </button>
              <button
                onClick={sendOrder}
                disabled={sending || !buildDate}
                className="px-4 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {sending ? "Sending…" : calResult ? "Send the order to SRS" : "Saved it — send the order to SRS"}
              </button>
            </>
          )}
          {phase === "done" && (
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded bg-slate-800 text-white text-sm font-medium hover:bg-slate-900"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
